/**
 * Module #36 — Tax Profiles admin. Tenant-scoped tax profiles (a set of named
 * rates with a remittance authority + which bases they tax) applied to an
 * auction; the invoice generator captures tax per line from the assigned
 * profile. Mirrors the AdminTaxonomies / consignment CRUD pattern.
 */

import { Badge, Button, HelpTooltip } from '@auctisync/ui';
import { useEffect, useState } from 'react';
import type { Json, Tables } from '@auctisync/shared';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../components/Toast';
import {
  ProfileAdminPage,
  Panel,
  InlineError,
  SkeletonList,
  PercentInput,
  listStyle,
  inputStyle,
  editorBoxStyle,
  readRowStyle,
} from './profileKit';
import {
  bpsToPct,
  rateToRow,
  newRate,
  validateTax,
  profileSaveError,
  type RateDraft,
} from './profileForms';

type Profile = Tables<'tax_profiles'>;
type Rate = Tables<'tax_rates'>;

export function AdminTaxProfiles() {
  return (
    <ProfileAdminPage
      title="Tax Profiles"
      subtitle="Reusable sets of sales-tax rates applied to an auction; captured per invoice line at generation."
      helpSlug="tax-profiles"
      learnMore="About tax profiles"
      emptyDescription="Tax profiles are tenant-scoped. Pick a tenant above to manage their profiles."
    >
      {(tenantId) => <TaxProfilesPanel tenantId={tenantId} />}
    </ProfileAdminPage>
  );
}

function TaxProfilesPanel({ tenantId }: { tenantId: string }) {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [ratesByProfile, setRatesByProfile] = useState<Record<string, Rate[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function refresh() {
    const [{ data: profs, error: pErr }, { data: rates, error: rErr }] = await Promise.all([
      supabase
        .from('tax_profiles')
        .select('*')
        .eq('tenant_id', tenantId)
        .is('archived_at', null)
        .order('is_default', { ascending: false })
        .order('name', { ascending: true }),
      supabase
        .from('tax_rates')
        .select('*')
        .eq('tenant_id', tenantId)
        .order('sort_order', { ascending: true }),
    ]);
    if (pErr || rErr) {
      setError((pErr ?? rErr)?.message ?? 'Failed to load.');
      return;
    }
    setProfiles(profs ?? []);
    const grouped: Record<string, Rate[]> = {};
    for (const r of rates ?? []) (grouped[r.profile_id] ??= []).push(r);
    setRatesByProfile(grouped);
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  async function handleArchive(id: string) {
    if (!window.confirm('Archive this tax profile? Captured invoice taxes are unaffected.')) return;
    const { error: err } = await supabase
      .from('tax_profiles')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setProfiles((prev) => prev.filter((p) => p.id !== id));
  }

  return (
    <Panel
      title="Tax profiles"
      subtitle="Each profile holds one or more rates, captured per invoice line when an auction uses it."
      helpSlug="tax-profiles"
      onAddClick={() => setEditingId('new')}
    >
      {error && <InlineError>{error}</InlineError>}
      {editingId === 'new' && (
        <TaxProfileEditor
          tenantId={tenantId}
          profile={null}
          rates={[]}
          existingNames={profiles.map((p) => p.name)}
          onCancel={() => setEditingId(null)}
          onSaved={async () => {
            await refresh();
            setEditingId(null);
          }}
        />
      )}
      {loading ? (
        <SkeletonList />
      ) : profiles.length === 0 && editingId === null ? (
        <span style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
          No tax profiles yet. Add one and assign it to an auction to start capturing tax.
        </span>
      ) : (
        <ul style={{ ...listStyle, gap: '0.5rem' }}>
          {profiles.map((p) =>
            editingId === p.id ? (
              <li key={p.id}>
                <TaxProfileEditor
                  tenantId={tenantId}
                  profile={p}
                  rates={ratesByProfile[p.id] ?? []}
                  existingNames={profiles.filter((x) => x.id !== p.id).map((x) => x.name)}
                  onCancel={() => setEditingId(null)}
                  onSaved={async () => {
                    await refresh();
                    setEditingId(null);
                  }}
                />
              </li>
            ) : (
              <li key={p.id}>
                <ProfileRow
                  profile={p}
                  rates={ratesByProfile[p.id] ?? []}
                  onEdit={() => setEditingId(p.id)}
                  onArchive={() => void handleArchive(p.id)}
                />
              </li>
            ),
          )}
        </ul>
      )}
    </Panel>
  );
}

function ProfileRow({
  profile,
  rates,
  onEdit,
  onArchive,
}: {
  profile: Profile;
  rates: Rate[];
  onEdit: () => void;
  onArchive: () => void;
}) {
  const summary = rates
    .map((r) => `${r.name} ${bpsToPct(r.rate_bps)}%`)
    .slice(0, 3)
    .join(' · ');
  return (
    <div style={readRowStyle}>
      <div style={{ flex: '1 1 20rem', minWidth: 0 }}>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <strong style={{ color: 'var(--text)' }}>{profile.name}</strong>
          {profile.is_default && <Badge variant="success">Default</Badge>}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
          {rates.length} rate{rates.length === 1 ? '' : 's'}
          {summary ? ` · ${summary}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '0.35rem' }}>
        <Button type="button" size="sm" variant="outline" onClick={onEdit}>
          Edit
        </Button>
        <Button type="button" size="sm" variant="destructive" onClick={onArchive}>
          Archive
        </Button>
      </div>
    </div>
  );
}

function rateToDraft(r: Rate): RateDraft {
  return {
    name: r.name,
    authority: r.authority ?? '',
    percent: bpsToPct(r.rate_bps),
    apply_to_bid: r.apply_to_bid,
    apply_to_premium: r.apply_to_premium,
    apply_to_expenses: r.apply_to_expenses,
    is_active: r.is_active,
  };
}

function TaxProfileEditor({
  tenantId,
  profile,
  rates,
  existingNames,
  onCancel,
  onSaved,
}: {
  tenantId: string;
  profile: Profile | null;
  rates: Rate[];
  existingNames: string[];
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const [name, setName] = useState(profile?.name ?? '');
  const [description, setDescription] = useState(profile?.description ?? '');
  const [isDefault, setIsDefault] = useState(profile?.is_default ?? false);
  const [rateDrafts, setRateDrafts] = useState<RateDraft[]>(() =>
    rates.length > 0 ? rates.map(rateToDraft) : [newRate()],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  function setRate(idx: number, patch: Partial<RateDraft>) {
    setRateDrafts((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  async function handleSave() {
    if (saving) return; // re-entrancy guard
    const errs = validateTax(name, rateDrafts, existingNames);
    if (errs.length > 0) {
      setError(errs[0] ?? 'Please fix the highlighted fields.');
      return;
    }
    setError(null);
    setSaving(true);

    // One atomic RPC (demote-if-default + upsert + replace rates) — rolls back
    // entirely on failure, so a tenant is never stranded without a default.
    // gen-types over-narrows nullable args to `string`/`Json`; runtime values
    // are valid for the SQL (the fn handles null).
    const { data, error: err } = await supabase.rpc('save_tax_profile', {
      p_id: (profile?.id ?? null) as unknown as string,
      p_tenant_id: tenantId,
      p_name: name.trim(),
      p_description: (description.trim() === '' ? null : description.trim()) as unknown as string,
      p_is_default: isDefault,
      p_rates: rateDrafts.map((r, i) => rateToRow(r, i)) as unknown as Json,
    });
    setSaving(false);
    if (err || !data) {
      setError(err ? profileSaveError(err) : 'Could not save the profile.');
      return;
    }

    setSaving(false);
    toast.success('Tax profile saved.');
    await onSaved();
  }

  return (
    <div style={editorBoxStyle}>
      <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: '2fr auto' }}>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Profile name (e.g., NY Sales Tax)"
          aria-label="Profile name"
          style={inputStyle}
        />
        <label style={defaultLabelStyle}>
          <input
            type="checkbox"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          Default
          <HelpTooltip helpSlug="tax-profiles">
            Only one tax profile per tenant can be the default.
          </HelpTooltip>
        </label>
      </div>

      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        rows={2}
        style={{ ...inputStyle, resize: 'vertical' }}
      />

      <div>
        <div style={subheadStyle}>Rates</div>
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {rateDrafts.map((rate, idx) => (
            <div key={idx} className={rateRowClass}>
              <input
                type="text"
                value={rate.name}
                onChange={(e) => setRate(idx, { name: e.target.value })}
                placeholder="Rate name (e.g., NY State)"
                aria-label="Rate name"
                style={inputStyle}
              />
              <input
                type="text"
                value={rate.authority}
                onChange={(e) => setRate(idx, { authority: e.target.value })}
                placeholder="Authority (optional)"
                aria-label="Remittance authority"
                style={inputStyle}
              />
              <PercentInput
                value={rate.percent}
                onChange={(v) => setRate(idx, { percent: v })}
                ariaLabel="Rate percent"
              />
              <div style={basesStyle}>
                <label style={checkLabelStyle}>
                  <input
                    type="checkbox"
                    checked={rate.apply_to_bid}
                    onChange={(e) => setRate(idx, { apply_to_bid: e.target.checked })}
                  />
                  bid
                </label>
                <label style={checkLabelStyle}>
                  <input
                    type="checkbox"
                    checked={rate.apply_to_premium}
                    onChange={(e) => setRate(idx, { apply_to_premium: e.target.checked })}
                  />
                  premium
                </label>
                <label style={checkLabelStyle}>
                  <input
                    type="checkbox"
                    checked={rate.apply_to_expenses}
                    onChange={(e) => setRate(idx, { apply_to_expenses: e.target.checked })}
                  />
                  expenses
                </label>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setRateDrafts((prev) => prev.filter((_, i) => i !== idx))}
                disabled={rateDrafts.length === 1}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '0.5rem' }}>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setRateDrafts((prev) => [...prev, newRate()])}
          >
            + Add rate
          </Button>
        </div>
      </div>

      {error && <InlineError>{error}</InlineError>}

      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={saving} onClick={() => void handleSave()}>
          {saving ? 'Saving…' : profile ? 'Save changes' : 'Create profile'}
        </Button>
      </div>
    </div>
  );
}

const subheadStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
  marginBottom: '0.35rem',
};
// 8rem + 8rem + 6rem of hard minimums is wider than a phone, so the rate row
// only takes its column template from `md` up and stacks below that.
const rateRowClass =
  'grid grid-cols-1 items-center gap-[0.4rem] ' +
  'md:grid-cols-[minmax(8rem,1.3fr)_minmax(8rem,1fr)_minmax(6rem,0.7fr)_auto_auto]';
const basesStyle: React.CSSProperties = {
  display: 'flex',
  gap: '0.6rem',
  alignItems: 'center',
  flexWrap: 'wrap',
};
const checkLabelStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  fontSize: '0.8rem',
  color: 'var(--text)',
};
const defaultLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  fontSize: '0.85rem',
  color: 'var(--text)',
};
